// Orders get progress model
export interface PodOrder {
  order_id: number;
  cart_id?: string | null;
  store_name?: string | null;
  product_id: number;
  product_name?: string | null;
  customer_name?: string | null;
  image_url?: string | null;
  variant_name?: string | null;
  sku?: string | null;
  warehouse_id?: string | null;
  available_qty?: number | null;
  box_id?: string | null;
  rack_id?: string | null;
  project_id: number;
  ordered_qty: number;
  printing_type: string | null;
  mockup_url?: string | null;
  printing_url?: string | null;
  stock_issue_request_id: number | null;
  approved: number | null;
  raw_inward_id: number | null;
  raw_inward_ids: number[] | null;
  raw_total_received_quantity: number | null;
  raw_inward_date: string | null;
  print_outward_id: number | null;
  print_outward_ids: number[] | null;
  printer_id: number | null;
  printer_name: string | null;
  total_print_inwarded_qty:string | null; 
  total_rejected_quantity:string | null;
  total_quantity_sent: number | null;
  print_outward_date: string | null;
  print_inward_id: number | null;
  print_inward_ids: number[] | null;
  print_total_received_quantity: number | null;
  print_inward_date: string | null;
  shipment_id: number | null;
  shipment_ids: number[] | null;
  shipments?: { shipment_id: number; cancelled: boolean }[] | null;
  shipment_count: number;
  shipped_shipment_count: number;
  shipment_quantity: number | null;
  status: string;
  dispatch_date:string | null;
  order_date?: string | null;
  is_previously_allocated?: boolean;
  unassigned_at?: string | null;
  unassigned_by_admin_id?: number | null;
  unassigned_by_name?: string | null;
}

export const isSamePodOrderRow = (a: PodOrder, b: PodOrder): boolean =>
  a.order_id === b.order_id &&
  a.product_id === b.product_id &&
  a.stock_issue_request_id === b.stock_issue_request_id &&
  (a.sku ?? null) === (b.sku ?? null);

export const getPodOrderRowKey = (
  order: Pick<
    PodOrder,
    "order_id" | "product_id" | "stock_issue_request_id" | "sku"
  >,
): string =>
  `${order.order_id}-${order.product_id}-${order.stock_issue_request_id ?? "null"}-${order.sku ?? "null"}`;

export interface PodOrderListingResponse {
  list: PodOrder[];
  total_count: number;
  limit: number;
  total_pages: number;
}

export interface GetPodOrdersParams {
  page?: number;
  limit?: number;
  status?: string;
  sku?: string;
  customer_name?: string;
  start_date?: string | null;
  end_date?: string | null;
  dispatch_start_date?: string | null;
  dispatch_end_date?: string | null;
  sort_by?: string;
  sort_type?: "ASC" | "DESC";
  order_ids?: number[];
  shipment_ids?: number[];
  printing_type?: string;
  previously_allocated?: string;
}



// Printing Files body and responce model

export interface PodOrderPrintingFilesRequestItem {
  order_id: number;
  product_id: number;
}

export interface PodOrderPrintingFilesBody {
  items: PodOrderPrintingFilesRequestItem[];
}

// Mockups — GET /v1/pod-order/mockups
export interface PodOrderMockupsParams {
  order_id: number;
  product_id: number;
  sku: string;
}

export interface PodOrderMockupApi {
  id: number;
  order_id: number;
  product_id: number;
  sku: string;
  decoration_type: string;
  image_url: string;
  approved: number;
  approved_date?: string | null;
  created_date?: string | null;
  modified_date?: string | null;
}

export interface PodOrderMockupsResponse {
  order_id: number;
  product_id: number;
  sku: string;
  mockups: PodOrderMockupApi[];
}
export interface PodOrderBatchInventoryAllocateItem {
  stock_issue_request_id?: number;
  order_id?: number;
}

export interface PodOrderSetWarehouseBody {
  stock_issue_request_ids: number[];
  warehouse_id: string;
}

export interface PodOrderSetPrintingTypeBody {
  items: { order_id: number; product_id: number }[];
  printing_type: string;
}

export interface PodOrderBatchUnassignProcessedItem {
  order_id: number;
  unassigned: boolean;
  error?: string;
}

export interface PodOrderBatchUnassignResult {
  processed: PodOrderBatchUnassignProcessedItem[];
  summary: PodOrderBatchInventoryAllocateSummary;
}

export const parsePodOrderBatchUnassignResult = (
  apiResult: unknown,
): PodOrderBatchUnassignResult | null => {
  if (!apiResult || typeof apiResult !== "object") return null;
  const result = apiResult as PodOrderBatchUnassignResult;
  if (
    !Array.isArray(result.processed) ||
    !result.summary ||
    typeof result.summary.total !== "number"
  ) {
    return null;
  }
  return result;
};

export interface PodOrderBatchInventoryAllocateBody {
  items: PodOrderBatchInventoryAllocateItem[];
}

export interface PodOrderBatchInventoryAllocateProcessedItem {
  stock_issue_request_id: number;
  order_id?: number;
  approved?: boolean;
  error?: string;
}

export interface PodOrderBatchInventoryAllocateSummary {
  total: number;
  succeeded: number;
  failed: number;
}

export interface PodOrderBatchInventoryAllocateResult {
  processed: PodOrderBatchInventoryAllocateProcessedItem[];
  summary: PodOrderBatchInventoryAllocateSummary;
}

export const parsePodOrderBatchInventoryAllocateResult = (
  apiResult: unknown,
): PodOrderBatchInventoryAllocateResult | null => {
  if (!apiResult || typeof apiResult !== "object") return null;
  const result = apiResult as PodOrderBatchInventoryAllocateResult;
  if (
    !Array.isArray(result.processed) ||
    !result.summary ||
    typeof result.summary.total !== "number" ||
    typeof result.summary.succeeded !== "number" ||
    typeof result.summary.failed !== "number"
  ) {
    return null;
  }
  return result;
};

export const getPodOrderBatchInventoryAllocateFailures = (
  result: PodOrderBatchInventoryAllocateResult | null | undefined,
): PodOrderBatchInventoryAllocateProcessedItem[] =>
  result?.processed?.filter(item => Boolean(item.error)) ?? [];

export const formatPodOrderBatchInventoryAllocateErrors = (
  failures: PodOrderBatchInventoryAllocateProcessedItem[],
): string =>
  failures
    .map(item => {
      const prefix = item.order_id != null ? `Order ${item.order_id}: ` : "";
      return `${prefix}${item.error ?? "Allocation failed."}`;
    })
    .join("\n");

export interface PodOrderPrintingFileResponce {
  id: number;
  order_id: number;
  product_id: number;
  image_url: string;
  admin_id?: number | null;
  admin_name?: string | null;
  created_date?: string;
  modified_date?: string;
}

export interface PodOrderPrintingFilesResponseItem {
  order_id: number;
  product_id: number;
  stock_issue_request_id?: number | null;
  sku: string;
  product_name?: string | null;
  quantity: number;
  printing_files?: PodOrderPrintingFileResponce[];
}



// Printing Outward body model
export interface PodOrderBatchPrintOutwardItem {
  order_id: number;
  product_id: number;
  sku: string;
  quantity: number;
  stock_issue_request_id?: number;
}

export interface PodOrderBatchPrintOutwardBody {
  items: PodOrderBatchPrintOutwardItem[];
  printer_id: number;
  timeline: string;
  instructions: string;
}

/** Outward drawer → store confirm fields (subset of batch body, without `items`) */
export type PodOrderPrintOutwardConfirmPayload = Pick<
  PodOrderBatchPrintOutwardBody,
  "printer_id" | "timeline" | "instructions"
>;

// Printing Inward body model — POST /v1/pod-order/batch-print-inward
export interface PodOrderBatchPrintInwardBody {
  items: PodOrderBatchPrintInwardItem[];
}

export interface PodOrderBatchPrintInwardItem {
  order_id: number;
  product_id: number;
  print_outward_id: number;
  quantity: number;
  sku: string;
  printing_type: string;
  rejected_quantity: number;
  rejection_reason?: string;
  rejection_images?: string[];
}

// CSV import — POST /v1/pod-order/import-csv
export type PodOrderCsvImportRowStatus = "created" | "failed";

export interface PodOrderCsvImportResultRow {
  cart_id: string;
  status: PodOrderCsvImportRowStatus;
  order_id?: number;
  shipment_id?: number;
  project_request_id?: number;
  project_id?: number;
  stock_issue_request_id?: number;
  error?: string;
}

export interface PodOrderCsvImportIssue {
  row?: number;
  cart_id?: string;
  message: string;
}

export interface PodOrderCsvImportResult {
  total_carts: number;
  created_count: number;
  failed_count: number;
  results: PodOrderCsvImportResultRow[];
  errors?: PodOrderCsvImportIssue[];
}

export const parsePodOrderCsvImportResult = (
  apiResult: unknown,
): PodOrderCsvImportResult | null => {
  if (!apiResult || typeof apiResult !== "object") return null;
  const result = apiResult as PodOrderCsvImportResult;
  if (
    typeof result.total_carts !== "number" ||
    typeof result.created_count !== "number" ||
    typeof result.failed_count !== "number" ||
    !Array.isArray(result.results)
  ) {
    return null;
  }
  if (result.errors != null && !Array.isArray(result.errors)) {
    return null;
  }
  return result;
};

export const getPodOrderCsvImportFailures = (
  result: PodOrderCsvImportResult | null | undefined,
): PodOrderCsvImportResultRow[] =>
  result?.results?.filter(row => row.status === "failed") ?? [];

export const getPodOrderCsvImportIssues = (
  result: PodOrderCsvImportResult | null | undefined,
): PodOrderCsvImportIssue[] => {
  if (!result) return [];
  const listed = (result.errors ?? []).filter(
    issue => typeof issue?.message === "string" && issue.message.trim().length > 0,
  );
  if (listed.length > 0) return listed;
  return getPodOrderCsvImportFailures(result).map(row => ({
    cart_id: row.cart_id,
    message: row.error || "Failed to create this order.",
  }));
};

export const formatPodOrderCsvImportIssue = (
  issue: PodOrderCsvImportIssue,
): string => {
  const location: string[] = [];
  if (issue.row != null) location.push(`Row ${issue.row}`);
  if (issue.cart_id) location.push(`Cart ID ${issue.cart_id}`);
  return location.length > 0
    ? `${location.join(" · ")}: ${issue.message}`
    : issue.message;
};








